import React from "react";
import { auth, EMAIL_VERIFICATION_ACS } from "../firebase";
import { applyActionCode, reload, sendEmailVerification, signOut } from "firebase/auth";

type VerifyState =
    | { kind: "checking" }
    | { kind: "success" }
    | { kind: "already" }
    | { kind: "error"; code?: string; message?: string }
    | { kind: "need-login" }
    | { kind: "resent" };

type VerifyStateKind = VerifyState["kind"];


function extractVerifyParams(loc: Location | undefined = typeof window !== "undefined" ? window.location : undefined) {
    let mode: string | null = null;
    let oobCode: string | null = null;

    if (!loc) return { mode, oobCode };

    // 1) Direkt i queryn
    const qs = new URLSearchParams(loc.search || "");
    mode = qs.get("mode");
    oobCode = qs.get("oobCode");
    if (mode && oobCode) return { mode, oobCode };

    // 2) I #hash (vissa klienter lägger parametrar där)
    const hash = (loc.hash || "").replace(/^#\??/, "");
    if (hash) {
        const hq = new URLSearchParams(hash);
        mode = hq.get("mode") || mode;
        oobCode = hq.get("oobCode") || oobCode;
        if (mode && oobCode) return { mode, oobCode };
    }

    // 3) Inbäddat i 'link' ELLER 'continueUrl' → parsea och hämta parametrar därifrån
    const linkParam = qs.get("link") || qs.get("continueUrl");
    if (linkParam) {
        try {
            const inner = new URL(linkParam);
            const iq = inner.searchParams;
            mode = iq.get("mode") || mode;
            oobCode = iq.get("oobCode") || oobCode;

            // vissa varianter lägger parametrarna i inner-urlens hash
            if (!(mode && oobCode) && inner.hash) {
                const ih = new URLSearchParams(inner.hash.replace(/^#\??/, ""));
                mode = ih.get("mode") || mode;
                oobCode = ih.get("oobCode") || oobCode;
            }
            if (mode && oobCode) return { mode, oobCode };
        } catch {
            /* ignore parse errors */
        }
    }

    return { mode, oobCode };
}
   

export default function VerifyEmail(): JSX.Element {
    const [sending, setSending] = React.useState(false);
    const [state, setState] = React.useState<VerifyState>({ kind: "checking" });

    // Skydda mot dubbla körningar och state-uppdateringar efter unmount
    const didRunRef = React.useRef(false);
    const mountedRef = React.useRef(true);
    React.useEffect(() => {
        return () => {
            mountedRef.current = false;
        };
    }, []);

    // Debugg på ?debug=1
    const DEBUG =
        typeof window !== "undefined" &&
        new URLSearchParams(window.location.search).get("debug") === "1";

    // Plocka query-parametrar en gång
    const { mode, oobCode } = React.useMemo(() => extractVerifyParams(), []);


    React.useEffect(() => {
        if (didRunRef.current) return;
        didRunRef.current = true;

        // Säkerhetsnät trots App.tsx-guard: hantera bara verifyEmail med giltig oobCode
        // Säkerhetsnät trots App.tsx-guard: hantera bara verifyEmail med giltig oobCode
        if (mode !== "verifyEmail" || !oobCode) {
            // Redan verifierad i denna session?
            if (auth.currentUser?.emailVerified) {
                if (mountedRef.current) setState({ kind: "already" });
                return;
            }
            // Redan verifierad på annan enhet? (flagga satt vid success)
            if (localStorage.getItem("gw-email-verified") === "true") {
                if (mountedRef.current) setState({ kind: "already" });
                return;
            }

            if (DEBUG) console.log("[VerifyEmail] invalid query", { mode, oobCode });
            if (mountedRef.current) setState({ kind: "error", message: "Ogiltig" });
            return;
        }

        


        // Redan verifierad?
        // Redan verifierad?
        const u = auth.currentUser;
        if (u?.emailVerified) {
            if (DEBUG) console.log("[VerifyEmail] already verified");

            (async () => {
                try {
                    await u.getIdToken(true);
                    localStorage.setItem("gw-email-verified", "true");
                    new BroadcastChannel("gw-auth").postMessage({ type: "email-verified" });
                } catch { /* ignore */ }
                if (mountedRef.current) setState({ kind: "already" });
            })();

            return;
        }


        (async () => {
            try {
                if (DEBUG) console.log("[VerifyEmail] applying action code…");
                await applyActionCode(auth, oobCode);

                // Uppdatera användaren om inloggad
                if (auth.currentUser) {
                    await reload(auth.currentUser);
                    await auth.currentUser.getIdToken(true);
                }

                // Markera att verif lyckades (för multi-device fallback)
                try {
                    localStorage.setItem("gw-email-verified", "true");
                    new BroadcastChannel("gw-auth").postMessage({ type: "email-verified" });
                } catch {/*medvetet*/ }

                if (DEBUG) console.log("[VerifyEmail] success");
                if (mountedRef.current) setState({ kind: "success" });
            } catch (e: any) {
                const code: string = e?.code || "";
                if (DEBUG) console.warn("[VerifyEmail] error", code, e?.message);



                if (code === "auth/expired-action-code" || code === "auth/invalid-action-code") {
                    try {
                        await auth.currentUser?.reload();
                        if (auth.currentUser?.emailVerified) {
                            await auth.currentUser.getIdToken(true);
                        }
                        localStorage.setItem("gw-email-verified", "true");
                        new BroadcastChannel("gw-auth").postMessage({ type: "email-verified" }); // 🔹
                    } catch {/*OK*/ }
                    if (mountedRef.current) setState({ kind: "already" });
                    return;
                }


                // Vanliga multi-device-fall: länken redan använd/förbrukad
                if (code === "auth/expired-action-code" || code === "auth/invalid-action-code") {
                    if (mountedRef.current) setState({ kind: "already" });
                    return;
                }

                if (!auth.currentUser) {
                    if (mountedRef.current) setState({ kind: "need-login" });
                } else {
                    if (mountedRef.current)
                        setState({
                            kind: "error",
                            code,
                            message: "Länken kan vara ogiltig, förbrukad eller ha gått ut.",
                        });
                }
            }
        })();
    }, [DEBUG, mode, oobCode]);


    // Kör auto-reload i bakgrunden tills vi blir verifierade (ingen F5 behövs)
    React.useEffect(() => {
        // definiera vilka state som är "klara" eller där vi inte ska polla
        const terminalKinds = new Set<VerifyStateKind>([
            "success",    // verifierad
            "already",    // redan verifierad
            "error",      // ogiltig länk etc (du kan välja att INTE lägga den här om du vill fortsätta polla även vid error)
            "need-login", // ingen user inloggad
        ]);

        // Hämta nuvarande "kind" från ditt state-objekt
        // EXEMPEL: om du har `const [state, setState] = useState({ kind: "..." })`
        // så läs 'state.kind' här:
        const kind = state.kind as VerifyStateKind;

        // Om vi redan är "klara" → gör inget
        if (terminalKinds.has(kind)) return;

        let cancelled = false;
        const tick = async () => {
            try {
                // om ingen användare → ingen idé att polla
                if (!auth.currentUser) return;

                await auth.currentUser.reload();

                if (auth.currentUser.emailVerified && !cancelled) {
                    // Sätt ditt "klart"-state. Anpassa till din egen state-form om den skiljer sig.
                    setState({ kind: "success" } as any);
                    return;
                }
            } catch {
                // ignorerar transient-fel
            }
            if (!cancelled) {
                setTimeout(tick, 3000); // prova igen om 3 sek
            }
        };

        // starta första varvet
        const t = setTimeout(tick, 1500);

        return () => {
            cancelled = true;
            clearTimeout(t);
        };
        // ⚠️ beroenden: trigga om 'state.kind' ändras
    }, [state.kind]);

    // Reagera direkt om någon annan flik signalerar att verifikationen är klar
    React.useEffect(() => {
        const KEY = "gw-email-verified";
        const onStorage = async (e: StorageEvent) => {
            if (e.key !== KEY) return;
            try {
                await auth.currentUser?.reload();
                if (auth.currentUser?.emailVerified) {
                    setState({ kind: "success" } as any);
                }
            } catch { /* ignore */ }
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, []);



    const resend = async () => {
        if (sending) return; // debounce
        const u = auth.currentUser;
        if (!u) {
            setState({ kind: "need-login" });
            return;
        }
        setSending(true);
        try {
            if (DEBUG) console.log("[VerifyEmail] sending new link…");
            await sendEmailVerification(u, EMAIL_VERIFICATION_ACS);
            setState({ kind: "resent" });
        } catch (e: any) {
            if (DEBUG) console.warn("[VerifyEmail] resend error", e?.code, e?.message);
            setState({ kind: "error", code: e?.code, message: e?.message });
        } finally {
            setSending(false);
        }
    };

    return (
        <div style={{ maxWidth: 560, margin: "64px auto", padding: 16 }} className="gw-card">
            {state.kind === "checking" && <p>Verifierar länk…</p>}

            {state.kind === "success" && (
                <>
                    <h2 style={{ marginTop: 0 }}>E-post verifierad 🎉</h2>
                    <p>
                        Klart! Gå tillbaka till appen. Om du skapade kontot på en annan enhet/flik kan du växla tillbaka dit.
                    </p>
                    <div style={{ marginTop: 12 }}>
                        <button className="btn btn-primary" onClick={() => (window.location.href = "/")}>
                            Öppna appen
                        </button>
                    </div>
                </>
            )}

            {state.kind === "already" && (
                <>
                    <h2 style={{ marginTop: 0 }}>Redan verifierad</h2>
                    <p>Den här länken har redan använts – allt är i sin ordning. Växla tillbaka till appen (eller fortsätt här).</p>
                    <div style={{ marginTop: 12 }}>
                        <button className="btn btn-primary" onClick={() => (window.location.href = "/")}>
                            Öppna appen
                        </button>
                    </div>
                </>
            )}

            {state.kind === "need-login" && (
                <>
                    <h2 style={{ marginTop: 0 }}>Logga in för att fortsätta</h2>
                    <p>Logga in, sedan kan du begära en ny verifieringslänk.</p>
                    <div style={{ marginTop: 12 }}>
                        <button className="btn btn-primary" onClick={() => (window.location.href = "/")}>
                            Öppna appen
                        </button>
                    </div>
                </>
            )}

            {state.kind === "resent" && (
                <>
                    <h2 style={{ marginTop: 0 }}>Ny länk skickad</h2>
                    <p>Kolla din inkorg. Länken leder tillbaka till denna sida.</p>
                </>
            )}

            {state.kind === "error" && (
                <>
                    <h2 style={{ marginTop: 0 }}>Kunde inte verifiera länken</h2>
                    <p style={{ color: "var(--danger)" }}>
                        {state.code ? <code>{state.code}</code> : null}
                        {state.message ? " – " + state.message : ""}
                    </p>

                    <p>
                        Om du öppnat länken på en annan enhet eller inte är inloggad här kan systemet
                        inte bekräfta verifieringen.
                        <br />
                        <strong>Gå tillbaka till appen</strong> eller logga in där – din e-post är troligen redan verifierad.
                    </p>

                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                        <button className="btn btn-primary" onClick={() => (window.location.href = "/")}>
                            Öppna appen
                        </button>
                        <button className="btn" onClick={resend} disabled={sending}>
                            {sending ? "Skickar…" : "Skicka ny verifieringslänk"}
                        </button>
                    </div>
                </>
            )}


            {state.kind !== "checking" && state.kind !== "resent" && state.kind !== "error" && (
                <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button className="btn btn-secondary" onClick={() => (window.location.href = "/")}>
                        Till startsidan
                    </button>
                    <button
                        className="btn btn-outline"
                        onClick={async () => {
                            try {
                                await signOut(auth);
                            } catch (e) {
                                console.warn("signOut failed", e);
                            }
                            window.location.href = "/";
                        }}
                    >
                        Logga ut / Byt konto
                    </button>
                </div>
            )}
        </div>
    );
}
